/** Landscape presentation even when a mobile browser cannot lock the physical screen. */
import "./orientation.css";

let enabled = false;
let rotated = false;
let physicalWidth = 0;
let physicalHeight = 0;
let width = 0;
let height = 0;
let top = 0;
let lockPending = false;
/** A primary release still owns its target until the browser dispatches its native click. */
const typingPointers = new Map<number, { held: boolean; primary: boolean }>();

/** Ask the platform first; browsers may require fullscreen or omit this API altogether. */
export async function requestLandscape(): Promise<void> {
  const orientation = screen.orientation as ScreenOrientation & {
    lock?: (orientation: string) => Promise<void>;
  };
  if (!orientation?.lock || lockPending) return;
  lockPending = true;
  try {
    await orientation.lock("landscape");
  } catch {
    // The rotated page remains usable when the platform refuses an orientation lock.
  } finally {
    lockPending = false;
  }
}

function isTyping(): boolean {
  const active = document.activeElement;
  return (
    active instanceof HTMLElement && (active.matches("input, textarea, select") || active.isContentEditable)
  );
}

/** Called before renderer sizing, including on iPhones that rotate without dispatching resize. */
export function updateLandscapeViewport(): void {
  // Blurring an input must not move the button while a finger is still pressing it. Keep the
  // whole layout until the ensuing click has dispatched, including a keyboard resize meanwhile.
  if (typingPointers.size > 0) return;
  const nextWidth = window.innerWidth;
  const nextHeight = window.innerHeight;
  // Keep text fields aligned with the system keyboard, which a webpage cannot rotate.
  const typing = enabled && isTyping();
  const nextRotated = enabled && nextHeight > nextWidth && !typing;
  const nextLogicalHeight = nextRotated
    ? nextWidth
    : typing
      ? Math.min(nextHeight, window.visualViewport?.height ?? nextHeight)
      : nextHeight;
  const nextTop = typing ? (window.visualViewport?.offsetTop ?? 0) : 0;
  const root = document.documentElement;
  root.classList.toggle("landscape-input", typing);
  if (
    physicalWidth === nextWidth &&
    physicalHeight === nextHeight &&
    rotated === nextRotated &&
    height === nextLogicalHeight &&
    top === nextTop
  )
    return;
  physicalWidth = nextWidth;
  physicalHeight = nextHeight;
  rotated = nextRotated;
  width = rotated ? nextHeight : nextWidth;
  height = nextLogicalHeight;
  top = nextTop;
  root.classList.toggle("landscape-fallback", rotated);
  root.style.setProperty("--lache-width", `${width}px`);
  root.style.setProperty("--lache-height", `${height}px`);
  root.style.setProperty("--lache-top", `${top}px`);
  root.style.setProperty("--lache-physical-width", `${physicalWidth}px`);
  root.style.setProperty("--lache-vw", `${width / 100}px`);
  root.style.setProperty("--lache-vh", `${height / 100}px`);
  window.dispatchEvent(new Event("lacheviewportchange"));
}

/** Set once from device capabilities, independently of which controller is being used. */
export function setupLandscape(on: boolean): void {
  enabled = on;
  updateLandscapeViewport();
  window.addEventListener("resize", updateLandscapeViewport);
  window.visualViewport?.addEventListener("resize", updateLandscapeViewport);
  window.visualViewport?.addEventListener("scroll", updateLandscapeViewport);
  const clearReleasedPointers = (): void => {
    for (const [id, pointer] of typingPointers) if (!pointer.held) typingPointers.delete(id);
  };
  window.addEventListener(
    "pointerdown",
    (event) => {
      if (enabled && (isTyping() || typingPointers.size > 0)) {
        // A drag or long press may end without clicking. The next interaction replaces those
        // abandoned releases without moving its own target during pointerdown.
        clearReleasedPointers();
        typingPointers.set(event.pointerId, { held: true, primary: event.isPrimary !== false });
      }
    },
    { capture: true },
  );
  window.addEventListener(
    "pointerup",
    (event) => {
      const pointer = typingPointers.get(event.pointerId);
      if (!pointer) return;
      if (pointer.primary) pointer.held = false;
      else typingPointers.delete(event.pointerId);
      updateLandscapeViewport();
    },
    { capture: true },
  );
  window.addEventListener(
    "pointercancel",
    (event) => {
      typingPointers.delete(event.pointerId);
      updateLandscapeViewport();
    },
    { capture: true },
  );
  // A frame can run between touchend and the compatibility click on a busy phone. Only the
  // bubbling click proves the original button's handler has run; never release a reused held ID.
  window.addEventListener("click", (event) => {
    const id = (event as PointerEvent).pointerId;
    if (typeof id === "number" && id >= 0) {
      if (typingPointers.get(id)?.held === false) typingPointers.delete(id);
    } else clearReleasedPointers();
    updateLandscapeViewport();
  });
  window.addEventListener(
    "keydown",
    () => {
      clearReleasedPointers();
      updateLandscapeViewport();
    },
    { capture: true },
  );
  const clearTypingPointers = (): void => {
    typingPointers.clear();
    updateLandscapeViewport();
  };
  window.addEventListener("blur", clearTypingPointers);
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) clearTypingPointers();
  });
  document.addEventListener("focusin", updateLandscapeViewport);
  document.addEventListener("focusout", () => requestAnimationFrame(updateLandscapeViewport));
  document.addEventListener("fullscreenchange", () => {
    if (enabled && document.fullscreenElement) void requestLandscape();
  });
  if (enabled) void requestLandscape();
}

export function landscapeSize(): { width: number; height: number } {
  return { width: width || window.innerWidth, height: height || window.innerHeight };
}

/** Browser pointer coordinates to the game's horizontal layout coordinates. */
export function landscapePoint(x: number, y: number): { x: number; y: number } {
  return rotated ? { x: y, y: physicalWidth - x } : { x, y };
}
